import React, { useMemo } from "react";
import { Icons } from "../icons";
import { ROUTES } from "../../routes/constants";
import { PERMISSIONS, usePermissions } from "../stores/permissionsStore";
import { ButtonText } from "../components/MenuButton/styles";

// Tipos para la configuración de navegación
export interface NavigationItem {
  id: string;
  name: string;
  route: string;
  permission: string;
  permissions?: string[];
  released: boolean;
  hasPermission?: boolean;
  icon:
  | "iconHome"
  | "iconBoard"
  | "iconPeople"
  | "iconStethoscope"
  | "iconChartPie";
  modalLabel?: string;
  homeContent?: (args: { isHovered?: boolean }) => React.ReactNode;
}

// Configuración centralizada de navegación
export const NAVIGATION_CONFIG: NavigationItem[] = [
  {
    id: "HOME",
    name: "Home",
    route: ROUTES.HOME,
    permission: PERMISSIONS.canViewHome,
    released: true,
    icon: "iconHome",
    modalLabel: "HOME",
    homeContent: ({ isHovered }) => (
      <>
        <Icons name="iconHome" isHovered={isHovered} width={72} height={72} />
        <span>Home</span>
      </>
    ),
  },
  {
    id: "MEDICAL_RECORD",
    name: "Historias clínicas",
    route: ROUTES.MEDICAL_RECORD,
    permission: PERMISSIONS.canEnterClinicalHistory,
    permissions: [
      PERMISSIONS.canEnterClinicalHistory,
      PERMISSIONS.canLoadClinicalHistory,
    ],
    released: true,
    icon: "iconBoard",
    modalLabel: "HISTORIAS CLINICAS",
    homeContent: ({ isHovered }) => (
      <>
        <Icons name="iconBoard" isHovered={isHovered} width={72} height={72} />
        <ButtonText>{"Historias\nClínicas"}</ButtonText>
      </>
    ),
  },
  {
    id: "PATIENTS",
    name: "Pacientes",
    route: ROUTES.MPI,
    permission: PERMISSIONS.canViewMPI,
    released: true, // Released per HU
    icon: "iconPeople",
    modalLabel: "PACIENTES",
    homeContent: ({ isHovered }) => (
      <>
        <Icons name="iconPeople" isHovered={isHovered} width={72} height={72} />
        <ButtonText>Pacientes</ButtonText>
      </>
    ),
  },
  {
    id: "PROFESSIONALS",
    name: "Profesionales",
    route: ROUTES.PROFESSIONALS,
    permission: PERMISSIONS.canViewProfessionals,
    released: true,
    icon: "iconStethoscope",
    modalLabel: "PROFESIONALES",
    homeContent: ({ isHovered }) => (
      <>
        <Icons
          name="iconStethoscope"
          isHovered={isHovered}
          width={72}
          height={72}
        />
        <ButtonText>Profesionales</ButtonText>
      </>
    ),
  },
  {
    id: "DATA",
    name: "Datos",
    route: ROUTES.DATA_ANALYSIS,
    permission: PERMISSIONS.canViewDataAnalysis,
    released: true,
    icon: "iconChartPie",
    modalLabel: "DATOS",
    homeContent: ({ isHovered }) => (
      <>
        <Icons
          name="iconChartPie"
          isHovered={isHovered}
          width={72}
          height={72}
        />
        <ButtonText>{"Datos"}</ButtonText>
      </>
    ),
  },
];

// Hook para obtener elementos de navegación filtrados por permisos
export const useNavigationItems = () => {
  return NAVIGATION_CONFIG;
};

const hasNavigationPermission = (
  item: NavigationItem,
  userPermissions: string[] | null | undefined,
) => {
  const allowedPermissions = item.permissions ?? [item.permission];

  return allowedPermissions.some((permission) =>
    userPermissions?.includes(permission),
  );
};

export const isNavigationItemActive = (
  pathname: string,
  item: Pick<NavigationItem, "route">,
) =>
  item.route === ROUTES.HOME
    ? pathname === item.route
    : pathname.startsWith(item.route);

// Hook para obtener elementos del sidebar (incluye Home, filtra solo por permisos)
export const useSidebarItems = () => {
  const userPermissions = usePermissions();

  return useMemo(() => {
    return NAVIGATION_CONFIG.map((item) => ({
      ...item,
      hasPermission: hasNavigationPermission(item, userPermissions),
    }));
  }, [userPermissions]);
};

// Hook para obtener elementos del home (excluye Home, filtra por permisos)
export const useHomeItems = () => {
  const userPermissions = usePermissions();

  return useMemo(() => {
    return NAVIGATION_CONFIG
      .filter((item) => item.id !== "HOME")
      .map((item) => ({
        ...item,
        hasPermission: hasNavigationPermission(item, userPermissions),
      }));
  }, [userPermissions]);
};
